import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { createStory } from '../../services/supabase/story.service';

/** iPhone camera + some Android intents use HEIC/HEIF; include so the picker accepts them. */
const STORY_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,image/*';

export default function CreateStoryDialog(props: {
  open: boolean;
  onClose: () => void;
  authorId: string;
  onCreated: () => void;
}) {
  const { open, onClose, authorId, onCreated } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [saving, setSaving] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionHint, setPermissionHint] = useState<string | null>(null);
  const [requestingPermission, setRequestingPermission] = useState(false);

  /**
   * Camera capture must NOT live inside MUI Dialog content: the Paper uses CSS transforms, which
   * breaks WebKit’s file/camera return path (you get an empty FileList). Gallery still works
   * because it uses a different picker. Inputs are portaled to document.body; buttons call .click()
   * in the same tap handler (valid user activation on iOS).
   */
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  const canSubmit = !!file && !saving;

  const loadImageFromFile = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read image file'));
      };
      img.src = url;
    });

  const prepareImageForUpload = async (input: File): Promise<File> => {
    // Target: keep story uploads small for mobile/realtime feeds.
    const TARGET_BYTES = 400 * 1024; // ~0.4MB
    const HARD_MAX_BYTES = 1_000 * 1024; // ~1MB fallback ceiling

    // If already within limits, keep as-is.
    if (input.size <= TARGET_BYTES) return input;

    const toJpeg = async (canvas: HTMLCanvasElement, quality: number) => {
      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality)
      );
      return blob;
    };

    let sourceW = 0;
    let sourceH = 0;
    let drawSource: CanvasImageSource | null = null;
    let bitmap: ImageBitmap | null = null;

    try {
      bitmap = await createImageBitmap(input);
      sourceW = bitmap.width;
      sourceH = bitmap.height;
      drawSource = bitmap;
    } catch {
      try {
        const img = await loadImageFromFile(input);
        sourceW = img.naturalWidth;
        sourceH = img.naturalHeight;
        drawSource = img;
      } catch {
        return input;
      }
    }

    try {
      // Start reasonably sized then shrink if needed.
      let maxSide = 1280;
      let quality = 0.78;

      for (let attempt = 0; attempt < 6; attempt++) {
        if (!drawSource || sourceW < 1 || sourceH < 1) break;

        const scale = Math.min(1, maxSide / Math.max(sourceW, sourceH));
        const w = Math.max(1, Math.round(sourceW * scale));
        const h = Math.max(1, Math.round(sourceH * scale));

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) break;

        ctx.drawImage(drawSource, 0, 0, w, h);

        const blob = await toJpeg(canvas, quality);
        if (!blob) break;

        // Success thresholds
        if (blob.size <= TARGET_BYTES) {
          bitmap?.close?.();
          return new File([blob], `story_${Date.now()}.jpg`, { type: 'image/jpeg' });
        }

        // If it's not even under 1MB, be more aggressive.
        if (blob.size > HARD_MAX_BYTES) {
          maxSide = Math.max(640, Math.round(maxSide * 0.78));
          quality = Math.max(0.5, quality - 0.12);
          continue;
        }

        // Under 1MB but still above target: step quality down first, then size.
        if (quality > 0.58) quality = Math.max(0.52, quality - 0.08);
        else maxSide = Math.max(640, Math.round(maxSide * 0.85));
      }

      bitmap?.close?.();
      return input;
    } catch {
      bitmap?.close?.();
      return input;
    }
  };

  const handleClose = () => {
    if (saving) return;
    setFile(null);
    setCaption('');
    setError(null);
    setPermissionHint(null);
    onClose();
  };

  const handleDialogClose = (
    _event: unknown,
    reason?: 'backdropClick' | 'escapeKeyDown'
  ) => {
    // On some Android Chrome flows (camera capture), returning to the page can
    // trigger an unintended dialog close. Only allow explicit Cancel.
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    handleClose();
  };

  const handleSubmit = async () => {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      await createStory({ authorId, file, caption: caption.trim() || null });
      onCreated();
      handleClose();
    } catch (e: any) {
      const msg =
        e?.message ||
        e?.error_description ||
        (typeof e === 'string' ? e : JSON.stringify(e)) ||
        'Failed to create story.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePickedFile = async (files: FileList | null | undefined) => {
    const f = files?.[0] || null;
    if (!f) {
      setError(
        'No photo was received. Try “Gallery”, or use “Allow camera” below, or check Chrome site settings (Camera allowed for this site).'
      );
      return;
    }
    if (f.size < 1) {
      setError('The photo file was empty. Try taking the picture again.');
      return;
    }
    setError(null);
    setPreparing(true);
    try {
      const prepared = await prepareImageForUpload(f);
      setFile(prepared);
    } catch (e: any) {
      setError(e?.message || 'Could not process this image. Try again or pick a different photo.');
    } finally {
      setPreparing(false);
    }
  };

  /**
   * Android Chrome often does not show a permission bar for `<input capture>`.
   * Calling getUserMedia once explicitly triggers the camera permission prompt.
   * Must be its own tap (do not await this before programmatic file input.click — that loses the user gesture).
   */
  const requestCameraPermission = async () => {
    setPermissionHint(null);
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermissionHint(
        'This browser does not expose the camera API. Use Upload / Camera or open the site in Chrome over HTTPS.'
      );
      return;
    }
    setRequestingPermission(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      stream.getTracks().forEach((t) => t.stop());
      setPermissionHint(
        'Camera access is OK. Use “Take photo” for the camera, or “Gallery” to pick an existing photo.'
      );
    } catch (e: any) {
      setPermissionHint(
        e?.message ||
          'Camera permission was blocked. In Chrome: tap the lock icon → Site settings → Camera → Allow. You need HTTPS (not HTTP) for the camera.'
      );
    } finally {
      setRequestingPermission(false);
    }
  };

  const filePickersPortal =
    open &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: 0,
          height: 0,
          opacity: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
        aria-hidden
      >
        {isMobile && (
          <input
            ref={cameraInputRef}
            type="file"
            accept={STORY_IMAGE_ACCEPT}
            capture="environment"
            onClick={(e) => {
              (e.target as HTMLInputElement).value = '';
            }}
            onChange={(e) => void handlePickedFile((e.target as HTMLInputElement).files)}
          />
        )}
        <input
          ref={galleryInputRef}
          type="file"
          accept={STORY_IMAGE_ACCEPT}
          onClick={(e) => {
            (e.target as HTMLInputElement).value = '';
          }}
          onChange={(e) => void handlePickedFile((e.target as HTMLInputElement).files)}
        />
      </div>,
      document.body
    );

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      {filePickersPortal}
      <DialogTitle
        sx={{
          pb: isMobile ? 1 : 2,
          position: isMobile ? 'sticky' : 'static',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: isMobile ? '1px solid' : 'none',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Box sx={{ fontWeight: 800 }}>Create story</Box>
        {isMobile && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={saving}
            edge="end"
            size="large"
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: isMobile ? 1 : 1.5, pb: isMobile ? 12 : 3 }}>
        {(saving || preparing) && <LinearProgress sx={{ mb: 2 }} />}

        {isMobile && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              If nothing happens after you snap a photo, the browser may not have camera access yet.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => void requestCameraPermission()}
                disabled={saving || preparing || requestingPermission}
                sx={{ textTransform: 'none' }}
              >
                {requestingPermission ? 'Asking for camera…' : 'Allow camera (tap once)'}
              </Button>
              {permissionHint && (
                <Typography variant="body2" color="text.secondary">
                  {permissionHint}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Then use <strong>Take photo</strong> or <strong>Gallery</strong>. Site must be served over{' '}
                <Link
                  href="https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#secure_context"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  HTTPS
                </Link>
                .
              </Typography>
            </Box>
          </Alert>
        )}

        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: isMobile ? 1.5 : 2,
            display: 'flex',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: isMobile ? 1.25 : 2,
            mb: 2,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }}>
              Add an image
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {file ? file.name : 'JPG/PNG recommended'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 1,
              flexShrink: 0,
              justifyContent: 'flex-end',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <Button
              variant="contained"
              startIcon={<PhotoCameraIcon />}
              disabled={saving || preparing}
              fullWidth={isMobile}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
              }}
              onClick={() => {
                if (isMobile) {
                  cameraInputRef.current?.click();
                } else {
                  galleryInputRef.current?.click();
                }
              }}
            >
              {isMobile ? 'Take photo' : 'Choose image'}
            </Button>
            {isMobile && (
              <Button
                variant="outlined"
                startIcon={<PhotoLibraryOutlinedIcon />}
                disabled={saving || preparing}
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                }}
                onClick={() => galleryInputRef.current?.click()}
              >
                Gallery
              </Button>
            )}
          </Box>
        </Box>

        {previewUrl && (
          <Box
            sx={{
              mb: 2,
              borderRadius: isMobile ? 2 : 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Box
              component="img"
              src={previewUrl}
              alt="Story preview"
              sx={{
                width: '100%',
                height: isMobile ? 'min(55vh, 420px)' : 280,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        )}

        <TextField
          label="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          disabled={saving}
        />

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: isMobile ? 2 : 3,
          py: 1.5,
          position: isMobile ? 'fixed' : 'static',
          left: isMobile ? 0 : undefined,
          right: isMobile ? 0 : undefined,
          bottom: isMobile ? 0 : undefined,
          bgcolor: 'background.paper',
          borderTop: isMobile ? '1px solid' : 'none',
          borderColor: 'divider',
          gap: 1,
          pb: isMobile ? 'calc(12px + env(safe-area-inset-bottom))' : 1.5,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={saving || preparing}
          variant={isMobile ? 'outlined' : 'text'}
          fullWidth={isMobile}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || preparing}
          variant="contained"
          fullWidth={isMobile}
          sx={{ borderRadius: 2 }}
        >
          Post story
        </Button>
      </DialogActions>
    </Dialog>
  );
}

